import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum BillingStatementLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  invoiceNumber = 'invoiceNumber',
  PoNumber = 'PoNumber',
  dueDate = 'dueDate',
  amount = 'amount',
  payments = 'payments', // payments made against the invoice
  invoiceBalance = 'invoiceBalance', // remaining balance on invoice - calculated in file
  lineBalance = 'lineBalance', // running balance (cumulative) of the line item - calculated in file
}

export const billingStatementAddressSchema = yup.object().shape({
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  postalCode: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
})
export type billingStatementAddress = yup.InferType<typeof billingStatementAddressSchema>

export const billingStatementFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: billingStatementAddressSchema.nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  billingStatementNumber: yup.string().nullable().optional(),
  statementDate: yup.string().nullable().optional(),
  locale: yup.string().nullable().optional(),
  currency: yup.string().nullable().optional(),
  billTo: yup
    .object()
    .shape({
      companyName: yup.string().nullable().optional(),
      billingAddress: billingStatementAddressSchema.nullable().optional(),
      attn: yup.string().nullable().optional(),
      phoneNumber: yup.string().nullable().optional(),
      emailAddress: yup.string().nullable().optional(),
    })
    .required(),
  remittanceInstructions: yup
    .object()
    .shape({
      routingNumber: yup.string().nullable().optional(),
      accountNumber: yup.string().nullable().optional(),
      bankName: yup.string().nullable().optional(),
      bankAddress: billingStatementAddressSchema.nullable().optional(),
      beneficiaryName: yup.string().nullable().optional(),
      beneficiaryAddress: billingStatementAddressSchema.nullable().optional(),
    })
    .required(),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        invoiceNumber: yup.string().nullable().optional(),
        PoNumber: yup.string().nullable().optional(),
        dueDate: yup.string().nullable().optional(),
        amount: yup.string().nullable().optional(),
        payments: yup.string().nullable().optional(),
        invoiceBalance: yup.string().nullable().optional(),
        lineBalance: yup.string().nullable().optional(),
      }),
    )
    .required(),
  notes: yup.string().nullable().optional(),
  footerText: yup.string().nullable().optional(),
})

export type BillingStatementFileData = yup.InferType<typeof billingStatementFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<BillingStatementFileData>

export const billingStatementFileMappings: RequiredFileData = {
  issuer: {
    companyName: 'd47d9645-e48e-478c-bae5-8d844bc283d5',
    address: {
      addressLine1: '4f66a580-943c-4ff8-bfcf-bd7d3e0eb7ab',
      addressLine2: 'cac58dca-7eb8-4874-941f-bb38b482139c',
      postalCode: '1af8d5eb-1a24-40e6-8baf-ab05c1121493',
      city: 'cb9fd909-565a-4de4-8544-eeba21c88f69',
      state: '0b561e3c-80c2-4711-af66-bc999fc7ff4a',
      country: 'b0978cd7-1f44-490c-b350-24b13eb40971',
    },
    phoneNumber: 'c8ed8d88-ad98-4d95-8dfa-6bb52a43fd03',
    emailAddress: '445b76e4-de0c-4aef-bc73-8e8698253742',
  },
  billingStatementNumber: 'af8eed4e-accb-42fe-80cf-0b19031417ac',
  statementDate: 'edaddda6-8eaf-41ab-b514-f9c1b9d00c76',
  locale: '85dcaf63-731a-48a4-976c-0a25c14be299',
  currency: 'bbab9ce6-67ef-4af9-9d76-f93edc471e95',
  billTo: {
    companyName: 'a0cb9987-ed39-4f60-bc1a-d77160ac1a43',
    billingAddress: {
      addressLine1: 'cc7541da-47b9-478c-b50e-6a1aa7f383e0',
      addressLine2: '5ae3d225-78c0-43a9-ad7d-4aa3265ff72d',
      postalCode: 'f58f4f1c-7e20-490f-9b7e-80913b63bf2f',
      city: 'dfea5a26-1c52-4669-83f0-18fbce53fc7b',
      state: 'cb1c8649-23b7-44be-9d1d-35729c213f4e',
      country: 'b1f9768b-ff25-495c-80b8-4863adc2b77b',
    },
    attn: '1f45ad0a-9da4-4653-9efb-197ec1401c79',
    phoneNumber: '119adb28-59d5-4e29-aa4e-5d839e28bbc6',
    emailAddress: 'cb35c06f-b80d-4cc7-a9ca-48a1ba2f6449',
  },
  remittanceInstructions: {
    routingNumber: '283d7b98-b033-4f24-92a3-4ff4bd5f4b7c',
    accountNumber: '1df9caf3-56dd-49a0-af99-2b6aa4ba9a2e',
    bankName: '3be8a127-be98-4b0b-82dc-dbf4f63d64da',
    bankAddress: {
      addressLine1: '366ca1d3-ab7c-4989-890a-1f5f1380c40d',
      addressLine2: 'b4af0d6e-cd4d-439c-8bd1-d0088dc5a8ac',
      postalCode: 'f566a524-5177-448c-9a87-3e9812af8b5a',
      city: 'b144b7b8-9794-44c1-b6f0-00b130a67f2a',
      state: '85a9eca8-26f3-474c-8a31-659052954251',
      country: '4940c396-aa3b-48e4-a677-eb10793618a3',
    },
    beneficiaryName: '9ea9c8e4-49be-4566-84d8-a5dd4df83cde',
    beneficiaryAddress: {
      addressLine1: 'c86365f6-9b40-43ed-86d5-a2940949a6b2',
      addressLine2: 'd62c2881-6c26-4f4b-8232-6fba418d2afe',
      postalCode: 'cc033646-8ab1-4944-9909-ba399b2597a4',
      city: 'd3d910e5-ebe2-4e35-a470-fab00c86eb77',
      state: '6266af87-a690-46a3-b06e-5b0c691ed5cb',
      country: '4d18e2ef-5332-4193-8053-2f4d0e20a000',
    },
  },
  items: [
    {
      itemNumber: null, // Kept as null since it's computed
      invoiceNumber: '07ca1cf5-26e6-4529-b66f-4fd45299e397',
      PoNumber: '48bf6ed8-cf1e-4f3e-9e44-60baca825f56',
      dueDate: 'ea158308-bb0e-48a2-a8eb-7f3fd63bb80c',
      amount: '9a79a8c0-52df-4be9-a6ea-c60f84181ea7',
      payments: '61b510e6-a725-49ab-9688-1986feec00e3',
      invoiceBalance: null, // Kept as null since it's computed
      lineBalance: null, // Kept as null since it's computed
    },
  ],
  notes: '794d024b-8524-4b19-9b06-80c4efb31a5f',
  footerText: 'f7a04f12-bf98-4808-96a4-16ac0748bacc',
}
